<template>
  <div :class="outerClasses">
    <div class="tw-h-full tw-flex tw-flex-row tw-w-full" :class="innerClasses">
      <SfAssistantChecksEmpty v-if="!hasChecks && !loading" />
      <template v-else>
        <div
          :class="[
            'tw-h-full tw-shrink-0 tw-grow tw-p-4 tw-w-condensed-sidepane tw-max-w-condensed-sidepane tw-overflow-y-auto',
            inModal
              ? 'tw-bg-white tw-border-0 tw-border-r tw-border-solid tw-border-sf-neutral-lighter-2'
              : 'tw-rounded-l-lg tw-bg-sf-neutral-subtle-1',
          ]"
        >
          <div v-if="loading">
            <SfSkeletonLoader
              v-for="i in 15"
              :key="i"
              height="2rem"
              :width="['10rem', '15rem', '20rem'][i % 3]"
              class="tw-px-2"
            />
          </div>
          <SfAssistantChecksLeftSidepane
            v-else
            :selected-category="selectedCategory"
            :selected-item="selectedItem"
            @select="setSelected"
          >
          </SfAssistantChecksLeftSidepane>
        </div>
        <div
          data-testid="assistant-checks-container"
          :class="[
            'tw-h-full tw-shrink tw-grow',
            inModal ? 'tw-bg-white' : 'tw-rounded-r-lg tw-bg-sf-snow-lighter',
            noFilterOrSearchResults
              ? 'tw-overflow-hidden'
              : 'tw-overflow-y-auto',
          ]"
        >
          <div
            v-if="dataStatus === 'READY_FOR_RELOAD'"
            class="
              tw-sticky tw-top-0 tw-z-50 tw-bg-sf-snow-lighter tw-px-6 tw-py-4
            "
          >
            <SfBannerInline
              variant="success"
              class="
                tw-block
                !tw-bg-emerald-100
                tw-border tw-border-solid tw-border-emerald-300
              "
            >
              <template #title>
                <span class="tw-font-semibold tw-mr-1">
                  {{
                    I18n.t(
                      'assistant_check.page.reload_stale_data_banner.title'
                    )
                  }}
                </span>
                <SfButtonInverted
                  :small="true"
                  :inline="true"
                  @click="reloadAfterCalculation"
                >
                  {{
                    I18n.t(
                      'assistant_check.page.reload_stale_data_banner.reload_button'
                    )
                  }}
                </SfButtonInverted>
              </template>
            </SfBannerInline>
          </div>
          <div
            v-else-if="dataStatus === 'CALCULATING'"
            class="
              tw-sticky tw-top-0 tw-z-50 tw-bg-sf-snow-lighter tw-px-6 tw-py-4
            "
          >
            <SfBannerInline
              :title="I18n.t('assistant_check.page.stale_data_banner.title')"
              variant="info"
              class="tw-block"
            />
          </div>
          <div
            v-else
            class="
              tw-sticky tw-top-0 tw-z-50 tw-bg-sf-snow-lighter tw-px-6 tw-pt-4
            "
          />
          <div v-if="loading">
            <RemarkSkeleton v-for="i in 4" :key="i" class="tw-mx-2 tw-my-1" />
          </div>
          <SfAssistantChecksContainer
            v-else
            :selected-category="selectedCategory"
            :selected-item="selectedItem"
          />
        </div>
      </template>
    </div>
  </div>
</template>

<script setup lang="ts">
import { computed, onMounted, watch } from 'vue'
import { storeToRefs } from 'pinia'

import { ChecksCalculationStatus } from '../types'
import RemarkSkeleton from '@app/shared/vue/components/molecules/remark/remark-skeleton.vue'
import SfAssistantChecksContainer from '@app/application/sf-assistant-checks/components/sf-assistant-checks-container.vue'
import SfAssistantChecksEmpty from '@app/application/sf-assistant-checks/components/sf-assistant-checks-empty.vue'
import SfAssistantChecksLeftSidepane from '@app/application/sf-assistant-checks/components/sf-assistant-checks-left-sidepane.vue'
import SfSkeletonLoader from '@app/shared/vue/components/sf-skeleton-loader.vue'
import { isUsingNewLayout } from '@app/adapters/silverfin'
import {
  ALL_CHECKS,
  UNREAD_CHECKS,
} from '@app/application/sf-assistant-checks/utils'
import { useSfAssistantChecksPage } from '../stores/page'

import { filteredCategory } from '../constants'
import SfBannerInline from '@app/shared/vue/components/sf-banner/sf-banner-inline.vue'
import SfButtonInverted from '@app/shared/vue/components/sf-button/sf-button-inverted.vue'
import Polling from '@app/utils/polling'
import useI18n from '@app/shared/vue/composables/use-I18n'
import useInboxUrlStorage from '@app/application/communication/screens/inbox/composables/use-inbox-url-storage'

const I18n = useI18n()

defineOptions({
  name: 'SfAssistantChecksPage',
})

interface Props {
  contextId: string | undefined
  inModal?: boolean
  initialCategory?: string
  initialItem?: string
  initialSearch?: string
}
const props = withDefaults(defineProps<Props>(), {
  inModal: false,
  initialCategory: UNREAD_CHECKS,
  initialItem: '',
  initialSearch: '',
})

const sfAssistantChecksPageStore = useSfAssistantChecksPage()
const {
  dataStatus,
  filteredUnreadCheckCount,
  loading,
  hasChecks,
  isFiltered,
  noFilterOrSearchResults,
} = storeToRefs(sfAssistantChecksPageStore)

if (props.contextId) {
  sfAssistantChecksPageStore.setWorkflowId(parseInt(props.contextId, 10))
}

sfAssistantChecksPageStore.fetchAll().then(() => {
  const triggerCalculation = sfAssistantChecksPageStore.triggerCalculation
  if (triggerCalculation.stale) {
    dataStatus.value = ChecksCalculationStatus.Calculating
  }

  const polling = new Polling(
    triggerCalculation.channel,
    triggerCalculation.lastId,
    () => {
      if (hasChecks.value) {
        dataStatus.value = ChecksCalculationStatus.ReadyForReload
      } else {
        reloadAfterCalculation()
      }
    }
  )
  polling.subscribe()
})

function reloadAfterCalculation(): void {
  sfAssistantChecksPageStore.fetchAll()
  dataStatus.value = ChecksCalculationStatus.UpToDate
}

const selectedCategory = useInboxUrlStorage(
  'category',
  props.initialCategory,
  (val) => val
)
const selectedItem = useInboxUrlStorage('item', props.initialItem, (val) => val)

const outerClasses = computed(() => {
  if (props.inModal) {
    return ['tw-h-full']
  }
  return [
    'tw-p-6',
    usingNewLayout
      ? 'tw-h-full'
      : 'two-drawer-layout__screen-height--after-secondary-subheader',
  ]
})

const innerClasses = computed(() => {
  if (props.inModal) {
    return []
  }
  const classes = ['tw-rounded-lg', 'tw-max-w-condensed-content', 'tw-mx-auto']
  if (hasChecks.value || loading.value) {
    classes.push(
      'tw-border',
      'tw-border-solid',
      'tw-border-sf-neutral-lighter-2'
    )
  }
  return classes
})
const usingNewLayout = isUsingNewLayout()

const setSelected = (category: string, item: string): void => {
  selectedCategory.value = category
  selectedItem.value = item
}

onMounted(() => {
  if (props.initialSearch) {
    sfAssistantChecksPageStore.searchFor(props.initialSearch)
  }
})

watch(
  [isFiltered, filteredUnreadCheckCount],
  ([newFiltered], [oldFiltered]) => {
    if (!hasChecks.value || loading.value) return

    const filteringCleared = !newFiltered && oldFiltered
    const filteringApplied = newFiltered && !oldFiltered

    // Switch to "All checks" if the user is on "Unread checks"
    // and there are no unread checks left anymore (after marking them as read)
    if (
      !isFiltered.value &&
      selectedCategory.value === UNREAD_CHECKS &&
      filteredUnreadCheckCount.value === 0
    ) {
      setSelected(ALL_CHECKS, '')
    }

    // Switch to "Unread checks"(page default) if the user cleared filtering
    if (filteringCleared) {
      setSelected(UNREAD_CHECKS, '')
    }

    if (filteringApplied && selectedCategory.value !== filteredCategory) {
      setSelected(filteredCategory, '')
    }
  }
)
</script>
